import { FC, useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, ButtonGroup, FormControl, FormLabel, Stack } from '@mui/material'
import { Info } from 'luxon'

import { BUTTON_GROUP_TOGGLE_SX } from '@/common/styles'
import { ISO_DAYS_OF_WEEK } from '@/common/utils/date'

// Seven equal cells rather than MUI's `fullWidth`, which sizes each button to its own
// content and so leaves the row ragged.
const FULL_WIDTH_CELLS_SX = { '& .MuiButton-root': { flex: '1 1 0', minWidth: 0 } }

export interface WeekdaysToggleProps {
  selectedDays: number[]
  onChange: (values: number[]) => void
  disabled?: boolean
  showLabel?: boolean
  /** Stretch the row to fill its container (narrow sidebars); off in the filter popover. */
  fullWidth?: boolean
}

export const WeekdaysToggle: FC<WeekdaysToggleProps> = ({
  selectedDays = [],
  onChange,
  disabled = false,
  showLabel = true,
  fullWidth = false,
}) => {
  const { t, i18n } = useTranslation()
  const labelId = useId()
  // Pass the active app locale so weekday labels follow the language switcher
  // instead of drifting to the system locale.
  const weekdayLabels = Info.weekdays('short', { locale: i18n.language })
  const label = t('common.daysOfTheWeek', 'Days Of The Week')

  return (
    <FormControl data-testid="weekday-toggle">
      <Stack spacing={1}>
        {showLabel && <FormLabel id={labelId}>{label}</FormLabel>}
        {/* `role="group"` needs a name of its own: `Mo`-`Su` say nothing about what they
            select. Callers that hide the label print their own heading above the row, but
            nothing associates that text with the group, so name it here either way. */}
        <ButtonGroup
          aria-label={showLabel ? undefined : label}
          aria-labelledby={showLabel ? labelId : undefined}
          disabled={disabled}
          fullWidth={fullWidth}
          sx={fullWidth ? [BUTTON_GROUP_TOGGLE_SX, FULL_WIDTH_CELLS_SX] : BUTTON_GROUP_TOGGLE_SX}
        >
          {ISO_DAYS_OF_WEEK.map(weekday => {
            const selected = selectedDays.includes(weekday)
            return (
              <Button
                variant={selected ? 'contained' : 'outlined'}
                aria-pressed={selected}
                onClick={event => {
                  event.preventDefault()
                  if (selected) {
                    onChange(selectedDays.filter(selectedDay => selectedDay !== weekday).sort())
                  } else {
                    onChange([...selectedDays, weekday].sort())
                  }
                }}
                key={weekday}
              >
                {weekdayLabels[weekday - 1].slice(0, 2)}
              </Button>
            )
          })}
        </ButtonGroup>
      </Stack>
    </FormControl>
  )
}
